import axios from "axios"
import React, { useEffect, useState } from "react"
import { useLocation, useParams } from "react-router-dom"
import Swal from "sweetalert2"
import AtsLogo from "../../src/assets/images/ats-logo.png"
import UsAtsImg2 from "../../src/assets/images/USATS2.png"
import { FaCheck, FaCheckCircle } from "react-icons/fa"

const defaultJobData = {
  company: "Bharat ATS",
  title: "Job Opportunity",
  companyDescription: "",
  recruiter: "Recruitment Team",
  location: "Not specified",
  experience: "Not specified",
  type: "Full-time",
  about: ["We would like to discuss this opportunity with you."],
  skills: [],
  responsibilities: [],
}

const decodeSkills = value => {
  if (!value) return []
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed.filter(Boolean) : []
  } catch {
    return value
      .split(",")
      .map(skill => skill.trim())
      .filter(Boolean)
  }
}
const convertTo12Hour = time => {
  const [h, m, s] = time.split(":")
  let hours = parseInt(h, 10)
  const ampm = hours >= 12 ? "PM" : "AM"
  hours = hours % 12 || 12
  const seconds = s ? `:${s}` : ""
  return `${hours}:${m}${seconds} ${ampm}`
}
const stripHtml = (value = "") =>
  value
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/\s+/g, " ")
    .trim()

const splitDescription = (value = "") => {
  const text = stripHtml(value)
  if (!text) return defaultJobData.about
  return text
    .split(/(?<=[.?!])\s+/)
    .map(item => item.trim())
    .filter(Boolean)
    .slice(0, 6)
}

const buildExperience = (minExp, maxExp) => {
  const min = minExp?.trim?.() || minExp
  const max = maxExp?.trim?.() || maxExp
  if (min && max) return `${min} - ${max} Years`
  if (min) return `${min}+ Years`
  if (max) return `Up to ${max} Years`
  return defaultJobData.experience
}

const parseConsentScheduleData = search => {
  const params = new URLSearchParams(search)

  return {
    jobId: params.get("job_id") || "",
    consentToken: params.get("consent_token") || "",
    company:
      params.get("company_name") ||
      params.get("client_name") ||
      defaultJobData.company,
    title: params.get("job_title") || defaultJobData.title,
    companyDescription:
      stripHtml(
        params.get("company_description") ||
          params.get("company_decsription") ||
          params.get("description") ||
          ""
      ) || defaultJobData.companyDescription,
    recruiter:
      params.get("recruiter_name") || defaultJobData.recruiter,
    location: params.get("location") || defaultJobData.location,
    experience:
      params.get("experience") || defaultJobData.experience,
    type: params.get("job_type") || defaultJobData.type,
    about: splitDescription(params.get("jd") || params.get("job_description") || ""),
    skills: decodeSkills(params.get("skills")),
    responsibilities: splitDescription(params.get("roles_responsibilities") || ""),
  }
}

const normalizeConsentJobData = (payload, fallback = {}) => {
  // console.info("Normalizing job data with payload:", payload, "and fallback:", fallback)
  const locationValue = Array.isArray(payload?.location)
    ? payload.location.join(", ")
    : payload?.location || fallback.location || defaultJobData.location
  const recruiterValue = Array.isArray(payload?.recruiters)
    ? payload.recruiters.join(", ")
    : Array.isArray(payload?.recruiters_names)
    ? payload.recruiters_names.join(", ")
    : payload?.recruiter_name || fallback.recruiter || defaultJobData.recruiter
  const jobType =
    payload?.workplace_type ||
    payload?.job_type ||
    payload?.employment_type ||
    payload?.type ||
    fallback.type ||
    defaultJobData.type
  const skillsValue = Array.isArray(payload?.skills)
    ? payload.skills.filter(Boolean)
    : decodeSkills(payload?.skills)

  return {
    jobId: payload?.job_id || payload?.id || fallback.jobId || "",
    consentToken: fallback.consentToken || "",
    company:
      payload?.company_name ||
      payload?.client_name ||
      fallback.company ||
      defaultJobData.company,
    title: payload?.job_title || fallback.title || defaultJobData.title,
    companyDescription:
      stripHtml(
        payload?.company_description ||
          payload?.company_decsription ||
          payload?.description ||
          fallback.companyDescription ||
          ""
      ) || defaultJobData.companyDescription,
    recruiter: recruiterValue,
    location: locationValue,
    experience: buildExperience(payload?.min_exp, payload?.max_exp),
    type: jobType,
    about: splitDescription(payload?.job_description || payload?.jd || ""),
    responsibilities: splitDescription(payload?.roles_responsibility || ""),
    skills: skillsValue,
  }
}

const buildScheduledAt = (date, time) => {
  const secondsTime = time.length === 5 ? `${time}:00` : time
  const localDate = new Date(`${date}T${secondsTime}`)
  const offsetMinutes = -localDate.getTimezoneOffset()
  const sign = offsetMinutes >= 0 ? "+" : "-"
  const absOffset = Math.abs(offsetMinutes)
  const offsetHours = String(Math.floor(absOffset / 60)).padStart(2, "0")
  const offsetMins = String(absOffset % 60).padStart(2, "0")

  return `${date}T${secondsTime}${sign}${offsetHours}:${offsetMins}`
}

const getConsentSwalOptions = (type, responseData) => {
  const fallbackOptions = {
    icon: type === "yes" ? "success" : "info",
    title: type === "yes" ? "Thank You!" : "Consent Declined",
    text:
      type === "yes"
        ? "You have given consent successfully."
        : "You have declined consent.",
    confirmButtonText: "OK",
    timer: 3000,
    timerProgressBar: true,
  }

  const responseHtml =
    typeof responseData === "string"
      ? responseData
      : responseData?.html || responseData?.message_html || null

  if (typeof responseHtml === "string" && /<[^>]+>/.test(responseHtml)) {
    return {
      icon: type === "yes" ? "success" : "info",
      html: responseHtml,
      confirmButtonText: "OK",
      timer: 3000,
      timerProgressBar: true,
    }
  }

  return fallbackOptions
}

function ConsentSchedule() {
  const location = useLocation()
  const { token1 } = useParams()
  const [step, setStep] = useState("main")
  const [prefDate, setPrefDate] = useState("")
  const [prefTime, setPrefTime] = useState("")
  const [slotError, setSlotError] = useState(false)
  const [scheduleLoading, setScheduleLoading] = useState(false)
  const [consentLoadingType, setConsentLoadingType] = useState(null)
  const [declineReason, setDeclineReason] = useState("")
  const [showDeclineInput, setShowDeclineInput] = useState(false)
  const [declineError, setDeclineError] = useState(false)
  const [activeTab, setActiveTab] = useState("jd")
  const userData = JSON.parse(sessionStorage.getItem("atsuser-info")) || {}
  const companies = userData?.user_details?.companies_data || [];
  const hasVgus = companies.some(
      (c) => c.company_domain === "vgus"
    );

    const logo = hasVgus ? UsAtsImg2 : AtsLogo;
  const [jobData, setJobData] = useState(() =>
    parseConsentScheduleData(location.search)
  )
  const [jobLoading, setJobLoading] = useState(
    Boolean(parseConsentScheduleData(location.search).jobId)
  )
  const [canSubmit, setCanSubmit] = useState(null)
  useEffect(() => {
    const checkConsentToken = async () => {
      const consentToken = token1 || jobData.consentToken
      if (!consentToken) {
        setCanSubmit(false)
        return
      }
      try {
        const response = await axios.get(
          `/api/v1/check-consent-token/?token=${consentToken}`
        )
        setCanSubmit(response.data?.can_submit !== false)
      } catch {
        setCanSubmit(false)
      }
    }
    checkConsentToken()
  }, [token1, jobData.consentToken])

  useEffect(() => {
    let isMounted = true
    const initialData = parseConsentScheduleData(location.search)

    const fetchConsentJobData = async () => {
      if (!initialData.jobId) {
        setJobLoading(false)
        return
      }

      setJobLoading(true)

      try {
        const response = await axios.get(
          `/api/v1/consent-job-data/${initialData.jobId}/`
        )
        const payload = response?.data?.data || response?.data || {}

        if (isMounted) {
          setJobData(normalizeConsentJobData(payload, initialData))
          setJobLoading(false)
        }
      } catch (error) {
        if (isMounted) {
          setJobData(prev => ({ ...prev, ...initialData }))
          setJobLoading(false)
        }
      }
    }

    fetchConsentJobData()

    return () => {
      isMounted = false
    }
  }, [location.search])

  useEffect(() => {
    if (location.search) {
      const params = new URLSearchParams(location.search)
      const nextParams = new URLSearchParams()
      if (params.get("job_id")) nextParams.set("job_id", params.get("job_id"))
      if (params.get("consent_token"))
        nextParams.set("consent_token", params.get("consent_token"))
      const nextQuery = nextParams.toString()
      window.history.replaceState(
        window.history.state,
        "",
        `${location.pathname}${nextQuery ? `?${nextQuery}` : ""}`
      )
    }
  }, [location.pathname, location.search])

  const handleConsentResponse = async type => {
    const consentToken = token1 || jobData.consentToken

    if (!consentToken) {
      Swal.fire({
        icon: "error",
        title: "Invalid Link",
        text: "This consent link is missing the required token.",
      })
      return
    }

    setConsentLoadingType(type)

    try {
      const response = await axios.post(
        `/api/v1/update-consent/${consentToken}/?response=${type}`
      )

      if (response.status === 200) {
        Swal.fire(getConsentSwalOptions(type, response.data)).then(() => {
          if (type === "no") {
            setStep("declined")
          }
        })
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Something went wrong",
      })
    } finally {
      setConsentLoadingType(null)
    }
  }

  const handleConfirmSlot = async () => {
    if (!prefDate || !prefTime) {
      setSlotError(true)
      return
    }

    const consentToken = token1 || jobData.consentToken

    if (!consentToken) {
      Swal.fire({
        icon: "error",
        title: "Invalid Link",
        text: "This schedule link is missing the required token.",
      })
      return
    }

    setSlotError(false)
    setScheduleLoading(true)

    try {
      await axios.post(`/api/v1/update-consent/${consentToken}/?response=yes`)

      await axios.post(`/api/v1/schedule-consent-call/${consentToken}/`, {
        scheduled_at: buildScheduledAt(prefDate, prefTime),
      })

      setStep("confirmed")
      Swal.fire({
        icon: "success",
        title: "Interview Scheduled",
        text: "Your interview slot has been scheduled successfully.",
        timer: 2500,
        showConfirmButton: false,
      })
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Scheduling Failed",
        text:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Unable to schedule the interview.",
      })
    } finally {
      setScheduleLoading(false)
    }
  }
  if (canSubmit === null) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f3f7fb",
        }}
      >
        <div style={{ textAlign: "center", color: "#103d63" }}>
          <span>Validating...</span>
        </div>
      </div>
    )
  }

  if (canSubmit === false) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          background: "#f3f7fb",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 520,
            background: "#fff",
            borderRadius: 28,
            padding: "52px 32px",
            textAlign: "center",
            boxShadow: "0 2px 16px rgba(0,0,0,0.08)",
          }}
        >
          {/* <img
            src={logo}
            alt="Bharat ATS"
            style={{ width: 220, maxWidth: "100%", marginBottom: 28 }}
          /> */}
          <FaCheckCircle style={{ fontSize: 48, color: "#103d63", marginBottom: 12 }} />
          <h2 style={{ color: "#103d63", fontWeight: 700, marginBottom: 12 }}>
            Already Responded
          </h2>
          <p style={{ color: "#64748b" ,fontSize: 16, lineHeight: 1.5}}>
            Thanks! You’ve already responded to this consent request. An agent will contact you at your selected time slot.
          </p>
        </div>
      </div>
    )
  }
  return (
    <>
      {/* <style>{styles}</style> */}
      <div className="cs-root">
        <div className="cs-card">
          <div className="cs-header">
            <div className="cs-kicker">Interview Invitation</div>
            {/* <div className="cs-company-name">{jobData.company}</div>
            <div className="cs-job-title">{jobData.title}</div>
           
            <div className="cs-company-desc">{jobData.companyDescription}</div> */}
          </div>

          <div className="cs-body">
            <div className="cs-section">
              <div className="cs-section-label">Company Name</div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 500,
                  lineHeight: 1.2,
                  color: "#103d63",
                  marginBottom: 0,
                }}
              >
                {jobData.company}
              </div>
            </div>

            {jobData.companyDescription && (
              <div className="cs-section">
                <div className="cs-section-label">Company Description</div>
                <p className="cs-about-text" style={{ color: "#103d63",}}>{jobData.companyDescription}</p>
              </div>
            )}
             <p
                className="cs-about-text"
                style={{ marginBottom: 10, fontWeight: 600, color: "#0f172a" }}
              >
                Role : {jobData.title}
              </p>
            {/* <div className="cs-section">
              <div className="cs-section-label">Job Description</div>
             
              {jobData.about.map((item, index) => (
                <p key={`${item}-${index}`} className="cs-about-text">
                  {item}
                </p>
              ))}
            </div> */}
            <div className="cs-tabs">
              <button
                className={activeTab === "jd" ? "active" : ""}
                onClick={() => setActiveTab("jd")}
              >
                Job Description
              </button>

              <button
                className={activeTab === "resp" ? "active" : ""}
                onClick={() => setActiveTab("resp")}
              >
                Responsibilities
              </button>

            </div>
            <div className="cs-section">
              <div className="cs-section-label">
                {activeTab === "jd" && "Job Description"}
                {activeTab === "resp" && "Responsibilities"}
                {activeTab === "skills" && "Key Skills"}
              </div>

              {activeTab === "jd" &&
                jobData.about.map((item, index) => (
                  <p key={index} className="cs-about-text">
                    {item}
                  </p>
                ))}

              {activeTab === "resp" &&
                (jobData.responsibilities?.length ? (
                  jobData.responsibilities.map((item, index) => (
                    <p key={index} className="cs-about-text">
                      {item}
                    </p>
                  ))
                ) : (
                  <p className="cs-about-text">No responsibilities provided.</p>
                ))}
            </div>

            <div className="cs-info-grid">
              <div className="cs-info-box">
                <div className="cs-info-label">Location</div>
                <div className="cs-info-value">{jobData.location}</div>
              </div>
              <div className="cs-info-box">
                <div className="cs-info-label">Experience</div>
                <div className="cs-info-value">{jobData.experience}</div>
              </div>
              <div className="cs-info-box">
                <div className="cs-info-label">Employment Type</div>
                <div className="cs-info-value">{jobData.type}</div>
              </div>
              <div className="cs-info-box">
                <div className="cs-info-label">Recruiter</div>
                <div className="cs-info-value">{jobData.recruiter}</div>
              </div>
            </div>

            {!!jobData.skills.length && (
              <div className="cs-section">
                <div className="cs-section-label">Required Skills</div>
                <div className="cs-skills-list">
                  {jobData.skills.map(skill => (
                    <span key={skill} className="cs-skill-chip">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <hr className="cs-divider" />

            {step === "main" && (
              <>
                <div className="cs-cta-title">
                  Are you interested in this opportunity?
                </div>
                <div className="cs-cta-sub">Pick a response below.</div>
              </>
            )}

            {step !== "confirmed" && step !== "declined" && (
              <>
              <button
                className="cs-btn-interested"
                onClick={e => {
                  setStep(prev => (prev === "schedule" ? "main" : "schedule"))
                  setShowDeclineInput(false)
                  setDeclineReason("")
                  e.stopPropagation()
                  // setStep("schedule");
                }}
              >
                <div className="cs-btn-icon green">Y</div>
                <div>
                  <div className="cs-btn-label">Yes, I&apos;m Interested</div>
                  <div className="cs-btn-sublabel">
                    Accept and choose an interview slot
                  </div>
                </div>
                <div className="cs-btn-arrow">›</div>
              </button>
              {step === "schedule" && (
                <>
                  <div className="cs-schedule-box">
                    <div className="cs-slot-row">
                      <div className="cs-slot-field">
                        <label>Preferred Date</label>
                        <input
                          type="date"
                          value={prefDate}
                          min={new Date().toISOString().split("T")[0]}
                          onChange={e => {
                            setPrefDate(e.target.value)
                            setSlotError(false)
                          }}
                        />
                      </div>
                      <div className="cs-slot-field">
                        <label>Preferred Time</label>
                        <input
                          type="time"
                          value={prefTime}
                          onChange={e => {
                            setPrefTime(e.target.value)
                            setSlotError(false)
                          }}
                        />
                      </div>
                    </div>
                    <div className="row" id="TemplateForJobOpportunity">
                      <div className="col-6 d-grid gap-2">
                        <button
                          className="btn btn btn-outline-secondary"
                          onClick={() => {
                            setStep("main")
                            setSlotError(false)
                          }}
                        >
                          Back
                        </button>
                      </div>
                      <div className="col-6 d-grid gap-2">
                        <button
                          className="btn  btn-success "
                          onClick={handleConfirmSlot}
                          disabled={scheduleLoading}
                        >
                          {scheduleLoading
                            ? "Scheduling..."
                            : "Confirm and Schedule Interview"}
                        </button>
                      </div>
                    </div>
                    <div
                      className="cs-slot-note"
                      style={{ marginTop: 12, fontSize: 13, color: "#64748b" }}
                    >
                      <strong>Note : </strong>Upon scheduling the interview, you
                      will receive the call.
                    </div>
                    <div className="cs-slot-actions d-none">
                      <button
                        className="cs-back-btn"
                        onClick={() => {
                          setStep("main")
                          setSlotError(false)
                        }}
                      >
                        Back
                      </button>
                      <button
                        className="cs-confirm-btn"
                        onClick={handleConfirmSlot}
                      >
                        Confirm and Schedule Interview
                      </button>
                    </div>
                    {slotError && (
                      <div className="cs-slot-error">
                        Please select both date and time.
                      </div>
                    )}
                  </div>
                </>
              )}
              <button
                className="cs-btn-decline"
                onClick={() => {
                  setShowDeclineInput(prev => !prev)
                  // Close schedule if open
                  setStep("main")
                }}
                disabled={scheduleLoading}
              >
                <div className="cs-btn-icon red">N</div>
                <div>
                  <div className="cs-btn-label">Not Interested</div>
                  <div className="cs-btn-sublabel">Tell us why</div>
                </div>
              </button>
              </>
            )}

            {showDeclineInput && (
              <div className="cs-decline-box">
                <label className="form-label mt-3">
                  Reason for not interested{" "}
                  <span className="text-danger">*</span>
                </label>

               <textarea
                    className="form-control"
                    rows={3}
                    value={declineReason}
                    onChange={e => {
                      const value = e.target.value
                      const cleanedValue = value.replace(/[^a-zA-Z0-9 .,!?-]/g, "")
                      setDeclineReason(cleanedValue)
                      setDeclineError(false)
                    }}
                    placeholder="Enter your reason..."
                  />
                {declineError && (
                  <div className="text-danger mt-1">Please enter a reason.</div>
                )}

                <div className="d-flex gap-2 mt-3 justify-content-center">
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowDeclineInput(false)
                      setDeclineReason("")
                      setDeclineError(false)
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    className="btn btn-danger"
                    onClick={async () => {
                      if (!declineReason.trim()) {
                        setDeclineError(true)
                        return
                      }

                      const consentToken = token1 || jobData.consentToken

                      try {
                        await axios.post(
                          `/api/v1/update-consent/${consentToken}/`,
                          {
                            response: "no",
                            reason: declineReason,
                          }
                        )

                        Swal.fire({
                          icon: "info",
                          title: "Response Submitted",
                          text: "Your response has been recorded.",
                        })

                        setShowDeclineInput(false)
                        setStep("declined")
                      } catch (err) {
                        Swal.fire({
                          icon: "error",
                          title: "Error",
                          text: "Something went wrong",
                        })
                      }
                    }}
                  >
                    Submit
                  </button>
                </div>
              </div>
            )}

            {step === "declined" && (
              <div className="cs-declined-box">
                <div className="cs-state-title">Response Recorded</div>
                <div className="cs-state-sub">
                  Thank you for letting us know. We will keep your profile for
                  future opportunities.
                </div>
              </div>
            )}

            {step === "confirmed" && (
              <div className="cs-confirmed-box">
                <div className="cs-state-title">Interview Scheduled</div>
                <div className="cs-state-sub">
                  Your slot has been selected for <strong>{prefDate}</strong> at{" "}
                  <strong>{convertTo12Hour(prefTime)}</strong>.
                </div>
              </div>
            )}

            <div className="cs-privacy">
              Your response is confidential and shared only with the hiring
              team.
            </div>
          </div>
        </div>

        <div className="cs-footer">Powered by Bharat ATS</div>
      </div>
    </>
  )
}

export default ConsentSchedule
